import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { PlatformUserRow } from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import {
  adminRevokeUserSessionsCallable,
  adminSetUserAccountStateCallable,
  wasReportedByCallable,
} from '@/lib/callables';

export type AccountAction = 'block' | 'unblock' | 'revokeSessions';

type Props = {
  action: AccountAction | null;
  user: PlatformUserRow | null;
  onClose: () => void;
  onDone: () => void;
};

/**
 * Confirmação das ações que mexem na conta de login, não no contato:
 * bloquear/desbloquear (`disabled` no Auth) e derrubar sessões. O reenvio do
 * link de verificação de e-mail saiu com a FD da #679 (#744): não há mais
 * verificação de contato nos clientes.
 *
 * O aviso de bloqueio é literal por um motivo: `disabled` vale para a
 * **identidade inteira**, não para uma empresa. A pessoa perde acesso a todas
 * as empresas dela de uma vez — quem clica precisa saber disso antes.
 *
 * Origem: #257 — commit b3bc5df63, 2026-08-11 17:08 +0200 (tela global de
 * Usuários).
 *
 * Contrato: `block`/`unblock` → `adminSetUserAccountState({ targetUid,
 * disabled })`; `revokeSessions` → `adminRevokeUserSessions({ targetUid })`.
 * Sucesso chama `onDone` (a página re-busca a lista, que não é listener) e
 * fecha. Erro: o confirm fica aberto com `Alert` genérico dentro dele e o erro
 * vai para `reportNonFatal('admin_account_action_failed')` (#1021).
 *
 * Falhas conhecidas:
 *  - 2026-10-25 (#1021): o erro saía só por toast — atrás do overlay do confirm,
 *    que continua aberto — e o `catch {}` descartava a causa. Nada aparecia.
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) erro dentro do confirm + report.
 *
 * Usado por: `routes/UsersPage.tsx`.
 *
 * Arquitetura: lógica em `apps/functions/src/admin/adminSetUserAccountState.ts`
 * e `adminRevokeUserSessions.ts`; gate de papel admin no servidor.
 *
 * Issues: #257, #744 (FD da #679 — reenvio de verificação removido), #1021
 * (erro dentro do confirm; mesmo achado do CRM, #174).
 */
export function AccountActionDialog({
  action,
  user,
  onClose,
  onDone,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!action || !user) return null;

  async function run(): Promise<void> {
    if (!action || !user) return;
    setSubmitting(true);
    setError(null);
    try {
      if (action === 'block' || action === 'unblock') {
        await adminSetUserAccountStateCallable({
          targetUid: user.uid,
          disabled: action === 'block',
        });
        toast.success(
          action === 'block'
            ? t('adminPlatformUsers.toast.blocked')
            : t('adminPlatformUsers.toast.unblocked'),
        );
      } else {
        await adminRevokeUserSessionsCallable({ targetUid: user.uid });
        toast.success(t('adminPlatformUsers.toast.sessionsRevoked'));
      }
      onDone();
      onClose();
    } catch (err) {
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `catch {}` + `toast.error` — o toast ficava
      // atrás do overlay do confirm (que segue aberto; mesmo achado do CRM, #174) e a causa se
      // perdia. Agora o erro aparece dentro do confirm e vai para a telemetria (sem e-mail/uid).
      // 2ª auditoria (2026-10-25 15:52 +0100, #1021): falha da callable já sai como `callable_failed`
      // pelo wrapper; aqui só o que ele não vê (Zod do input, offline) — senão eram 2 eventos.
      if (!wasReportedByCallable(err)) {
        reportNonFatal('admin_account_action_failed', { action, ...serializeError(err) });
      }
      setError(t('adminPlatformUsers.errors.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const name = user.displayName || user.email || user.uid;

  return (
    <AdminConfirm
      visible
      title={t(`adminPlatformUsers.accountDialog.title.${action}`)}
      confirmLabel={t(`adminPlatformUsers.accountDialog.confirm.${action}`)}
      loading={submitting}
      onConfirm={(): void => {
        void run();
      }}
      onDismiss={(): void => {
        if (submitting) return;
        setError(null);
        onClose();
      }}
    >
      <SpaceBetween size="s">
        <Box variant="p">{t(`adminPlatformUsers.accountDialog.body.${action}`, { name })}</Box>
        {action === 'block' ? (
          <Alert type="warning">{t('adminPlatformUsers.accountDialog.blockWarning')}</Alert>
        ) : null}
        {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </AdminConfirm>
  );
}
